import { AxiosError } from 'axios';

import rollbar from './rollbar';

// Helper to extract error message from 'catch' errors
type ErrorWithMessage = {
  message: string;
};

function isErrorWithMessage(error: unknown): error is ErrorWithMessage {
  return (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof (error as Record<string, unknown>).message === 'string'
  );
}

function toErrorWithMessage(maybeError: unknown): ErrorWithMessage {
  if (isErrorWithMessage(maybeError)) return maybeError;

  try {
    return new Error(JSON.stringify(maybeError));
  } catch {
    // fallback in case there's an error stringfying the maybeError
    // like with circular references for example.
    return new Error(String(maybeError));
  }
}

export function getErrorMessage(error: unknown) {
  return toErrorWithMessage(error).message;
}

export const shouldSkipLoggingForAuthError = (error: Error | AxiosError) => {
  if ('response' in error) {
    // Axios error
    const ignoreErrorList = [400, 401];
    if (error.response?.status && ignoreErrorList.includes(error.response.status)) {
      // Wrong email or password. Don't log it
      return true;
    }

    if (error.response?.headers['x-skip-logging']) {
      return true;
    }
  }
  return false;
};

export enum ErrorType {
  BadRequest = 'BAD_REQUEST',
  InternalError = 'INTERNAL_SERVER_ERROR',
  NotFound = 'NOT_FOUND',
  ValidationError = 'VALIDATION_ERROR',
  Unauthorized = 'UNAUTHORIZED',
}

const ERROR_STATUS_CODE_MAP: Record<ErrorType, number> = {
  [ErrorType.BadRequest]: 400,
  [ErrorType.Unauthorized]: 401,
  [ErrorType.ValidationError]: 422,
  [ErrorType.NotFound]: 404,
  [ErrorType.InternalError]: 500,
};

export interface AppErrorDetails {
  // Optional user facing error messages with the root cause of the error.
  root?: string[];

  // Optional user facing error messages for specific fields.
  fields?: Record<string, string[]>;

  // Allow additional properties
  [key: string]: unknown;
}

export interface AppErrorOptions {
  cause?: unknown;
  statusCode?: number;
  details?: AppErrorDetails;
  skipMessageFormatting?: boolean;
  skipLogging?: boolean;
}

export class AppError extends Error {
  type: ErrorType;
  readonly cause?: unknown;
  readonly statusCode: number;
  readonly details?: AppErrorDetails;
  readonly rollbarContext?: unknown;
  readonly skipLogging: boolean;

  constructor(
    errorType: ErrorType,
    message: string,
    { cause, details, statusCode, skipMessageFormatting, skipLogging }: AppErrorOptions = {},
  ) {
    super(skipMessageFormatting ? message : formatAppErrorMessage(message, cause));
    this.type = errorType;
    this.name = errorType;
    this.details = details;
    this.cause = cause;
    this.skipLogging = skipLogging || false;

    if (statusCode) {
      this.statusCode = statusCode;
    } else {
      this.statusCode = ERROR_STATUS_CODE_MAP[errorType] || 500;
    }

    this.rollbarContext = {
      type: this.type,
      statusCode: this.statusCode,
      details: this.details,
    };
  }

  /**
   * Determines whether the error should be logged based on its type.
   *
   * We should add rules here to avoid logging unwanted errors.
   */
  shouldLog(): boolean {
    if (this.skipLogging) return false;

    return (
      this.type !== ErrorType.ValidationError &&
      this.type !== ErrorType.BadRequest &&
      this.type !== ErrorType.Unauthorized
    );
  }

  static fromSerializedError(error: Error): AppError {
    let json: Record<string, unknown>;
    try {
      json = JSON.parse(error.message);
    } catch (error) {
      rollbar.error('Failed to parse JSON string in AppError.fromJSON', error as Error);
      throw new Error('Invalid JSON string provided to AppError.fromJSON');
    }

    const { type, message, statusCode, details, skipLogging } = json as {
      type: ErrorType;
      message: string;
      statusCode?: number;
      details?: AppErrorDetails;
      skipLogging?: boolean;
    };

    return new AppError(type, message, {
      cause: error,
      statusCode,
      details,
      skipLogging,
      skipMessageFormatting: true,
    });
  }

  toJSON(): Record<string, unknown> {
    return {
      type: this.type,
      message: this.message,
      statusCode: this.statusCode,
      details: this.details,
    };
  }

  /**
   * Returns a user-friendly error message.
   * This is used to display errors in the UI.
   *
   * @params fallbackMessage - Optional fallback message if no specific error details are available.
   */
  getUserMessage(fallbackMessage?: string): string {
    const details = this.getValidationErrorDetails();

    // Priority: root errors > field errors > general message
    if (details?.root && details.root?.length > 0) {
      return details.root[0];
    }

    if (details?.fields) {
      const fieldErrors = Object.values(details.fields).flat();
      if (fieldErrors.length > 0) {
        return fieldErrors[0];
      }
    }

    if (fallbackMessage) {
      return fallbackMessage;
    }

    // We only return the internal message if no user-friendly message is available.
    return this.message || 'Erro inesperado';
  }

  getValidationErrorDetails(): ValidationErrorDetails | undefined {
    const details = this.details;
    if (!details || (!details.root && !details.fields)) return undefined;

    return {
      root: details.root,
      fields: details.fields,
    };
  }
}

export interface ValidationErrorDetails {
  root?: string[];
  fields?: Record<string, string[]>;
}
/**
 * Formats an error message by appending additional details from the original error if provided.
 *
 * @param message - The main error message to be formatted.
 * @param originalError - An optional parameter representing the original error.
 * @returns The formatted error message.
 */
function formatAppErrorMessage(message: string, originalError?: unknown): string {
  if (!originalError) return message;
  if (typeof originalError === 'object' && 'message' in originalError) {
    return `${message} - ${originalError.message}`;
  }
  return `${message} - ${originalError}`;
}
